import { useEffect, useState } from 'react';
import { Dialog, DialogClose, DialogContent } from './dialog';
import { Button } from './button';
import { Field, Input } from './field';
import { Spinner } from './feedback';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  body?: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  /** When set, the button stays disabled until this exact word is typed. */
  requireTyped?: string;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  body,
  confirmLabel,
  destructive = false,
  requireTyped,
  pending = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const ready = !requireTyped || typed.trim().toUpperCase() === requireTyped.toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={title}
        description={description}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant={destructive ? 'destructive' : 'primary'}
              disabled={!ready || pending}
              onClick={() => void onConfirm()}
            >
              {pending ? <Spinner /> : null}
              {confirmLabel}
            </Button>
          </>
        }
      >
        {body}
        {requireTyped ? (
          <Field label={`Type ${requireTyped} to confirm`} className="mt-4">
            {(props) => (
              <Input
                {...props}
                value={typed}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setTyped(event.target.value)}
                placeholder={requireTyped}
              />
            )}
          </Field>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
